import React, {useEffect} from 'react'
import {Control, FieldErrors, UseFormRegister, UseFormSetValue, UseFormWatch} from 'react-hook-form'
import {IApplicationFormData} from '../../models/application-form'
import {TextInput} from '../../../common/component/TextInput'
import RepeatableEntryList from './sections/RepeatableEntryList'
import {createEmptyOtherOccupant} from './sections/petVehicleDraft'
import {otherOccupantPath} from './sections/petVehiclePath'
import WizardSectionCard from './sections/WizardSectionCard'
import {RadioButtonInput} from '../../../common/component/RadioButtonInput'
import {toOptionalBoolean} from '../wizard/wizardAccess'
import {SelectInput} from '../../../common/component/SelectInput'

interface OtherOccupantsStepProps {
  register: UseFormRegister<IApplicationFormData>
  control: Control<IApplicationFormData>
  errors?: FieldErrors<IApplicationFormData>
  setValue: UseFormSetValue<IApplicationFormData>
  watch: UseFormWatch<IApplicationFormData>
  triggerValidation?: () => Promise<boolean>
}

const occupantRelationshipOptions = [
  {label: 'Child', value: 'child'},
  {label: 'Partner', value: 'partner'},
  {label: 'Cohabiting partner (not on lease)', value: 'cohabiting partner'},
  {label: 'Parent', value: 'parent'},
  {label: 'Adult dependant', value: 'adult dependant'},
  {label: 'Sibling', value: 'sibling'},
  {label: 'Friend', value: 'friend'},
  {label: 'Other', value: 'other'},
]

const OtherOccupantsStep: React.FC<OtherOccupantsStepProps> = ({
  register,
  control,
  errors,
  setValue,
  watch,
  triggerValidation,
}) => {
  const otherOccupants = watch('otherOccupants') ?? []
  const hasOtherOccupants = toOptionalBoolean(watch('hasOtherOccupants'))

  const hasOtherOccupantsOptions = [
    {label: 'No', value: false},
    {label: 'Yes, others will live there too', value: true},
  ]

  const setOtherOccupantsState = (enabled: boolean) => {
    if (!enabled) {
      setValue(
        'removedOtherOccupants',
        [...(watch('removedOtherOccupants') ?? []), ...otherOccupants],
        {
          shouldDirty: true,
        }
      )
    }
    const nextList = enabled
      ? hasOtherOccupants === true && otherOccupants.length > 0
        ? otherOccupants
        : [createEmptyOtherOccupant()]
      : []
    setValue('hasOtherOccupants', enabled, {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: !enabled,
    })
    setValue('otherOccupants', nextList, {
      shouldDirty: true,
      shouldTouch: !enabled,
      shouldValidate: !enabled,
    })
    if (!enabled && triggerValidation) triggerValidation()
  }

  const addOtherOccupant = () => {
    setValue('otherOccupants', [...otherOccupants, createEmptyOtherOccupant()], {
      shouldDirty: true,
      shouldTouch: false,
      shouldValidate: false,
    })
  }

  const removeOtherOccupant = (index: number) => {
    setValue(
      'removedOtherOccupants',
      [...(watch('removedOtherOccupants') ?? []), otherOccupants[index]],
      {shouldDirty: true}
    )
    const nextList = otherOccupants.filter((_, oIndex) => oIndex !== index)
    setValue('otherOccupants', nextList, {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: true,
    })
  }

  useEffect(() => {
    if (triggerValidation) triggerValidation()
  }, [])

  return (
    <div>
      <div className='wizard-hint-card mb-4 d-flex ' style={{padding: '20px'}}>
        <i className='bi bi-info-circle me-5 gradient-icon'></i>
        <span>
          <strong>Not on the lease.</strong> List children, dependants, or anyone else who'll live
          with you but isn't signing the lease.
        </span>
      </div>

      <div className='d-grid gap-4'>
        <WizardSectionCard title="Anyone else living with you who isn't applying?" className='p-8'>
          <RadioButtonInput<IApplicationFormData>
            name='hasOtherOccupants'
            label=''
            className='mt-5'
            control={control}
            errors={errors}
            required
            options={hasOtherOccupantsOptions}
            value={hasOtherOccupants}
            defaultValue={hasOtherOccupants}
            onChange={setOtherOccupantsState}
          />

          {hasOtherOccupants && (
            <>
              <RepeatableEntryList
                items={otherOccupants}
                itemTitle='Occupant'
                addButtonText='Add Another  Occupant'
                onAdd={addOtherOccupant}
                onRemove={removeOtherOccupant}
                renderItemFields={(index) => (
                  <>
                    <TextInput
                      name={otherOccupantPath(index, 'name')}
                      label='Full name'
                      placeholder='e.g. Sam Nguyen'
                      register={register}
                      errors={errors}
                      required={true}
                    />

                    <div
                      className='d-grid gap-4'
                      style={{gridTemplateColumns: 'minmax(0, 2fr) minmax(0, 3fr)'}}
                    >
                      <TextInput
                        name={otherOccupantPath(index, 'age')}
                        label='Age'
                        placeholder='e.g. 8'
                        register={register}
                        errors={errors}
                        required={true}
                        numeric={true}
                        allowDecimal={false}
                      />

                      <SelectInput
                        name={otherOccupantPath(index, 'relationship')}
                        label='Relationship'
                        inputClassName='mt-2'
                        register={register}
                        errors={errors}
                        required
                        optionsList={occupantRelationshipOptions}
                      />
                    </div>
                  </>
                )}
              />
            </>
          )}
        </WizardSectionCard>
      </div>
    </div>
  )
}

export default OtherOccupantsStep
